<template>
  <div>
    <div
      class="relative rounded-l"
      :class="heightClass"
    >
      <!-- The photo where the venue has one; its own colour where it does not,
           so the banner never shows a broken image or an empty grey box. -->
      <div
        class="absolute inset-0 overflow-hidden rounded-l"
        :style="accentStyle"
      >
        <!-- A page's banner is the largest thing on it, so it is fetched first.
             The blade preloads the same candidate with the same `sizes`. -->
        <img
          v-if="hasCover"
          alt=""
          class="h-full w-full object-cover"
          decoding="async"
          :fetchpriority="isStrip ? undefined : 'high'"
          :sizes="COVER_SIZES"
          :src="props.coverUrl ?? undefined"
          :srcset="props.coverSrcset ?? undefined"
          @error="coverFailed = true"
        />
        <!-- A hairline drawn over the photo, so a cover with a white ground
             still has an edge against the white page. Neutral black or white,
             never the blue-grey hairline, which tints a photo's edge. -->
        <span
          aria-hidden="true"
          class="pointer-events-none absolute inset-0 rounded-l ring-1 ring-inset ring-image-outline"
        />
      </div>

      <!-- No photo: the logo centred on the venue's own colour. -->
      <span
        v-if="!hasCover"
        class="absolute inset-0 m-auto flex items-center justify-center overflow-hidden rounded-logo border-control border-bg shadow-chip"
        :class="soloLogoClass"
        :style="accentStyle"
      >
        <img
          v-if="hasLogo"
          alt=""
          class="h-full w-full object-cover"
          :src="props.logoUrl ?? undefined"
          @error="logoFailed = true"
        />
        <span
          v-else
          class="text-heading text-white"
        >
          {{ venueMonogram(props.name) }}
        </span>
      </span>

      <slot />
    </div>

    <!-- With a photo, the logo hangs half off the cover's foot, pinned there the
         way the directory card pins it. It used to share a bottom-aligned row
         with the caption, so a caption taller than the logo pushed the logo down
         and off the cover; on a phone any second line did it. Pinned, a long
         caption grows downwards and the logo stays where it is. -->
    <div
      v-if="hasCover"
      class="relative flex min-w-0 items-start"
      :class="rowClass"
    >
      <span
        class="absolute top-0 flex -translate-y-1/2 items-center justify-center overflow-hidden rounded-logo border-control border-bg shadow-chip"
        :class="logoClass"
        :style="accentStyle"
      >
        <img
          v-if="hasLogo"
          alt=""
          class="h-full w-full object-cover"
          :src="props.logoUrl ?? undefined"
          @error="logoFailed = true"
        />
        <span
          v-else
          class="text-heading text-white"
        >
          {{ venueMonogram(props.name) }}
        </span>
      </span>

      <!-- Holds the logo's column, so the caption starts beside it. A phone's
           banner drops it and gives the caption the full width under the logo. -->
      <span
        aria-hidden="true"
        class="shrink-0"
        :class="spacerClass"
      />

      <div
        v-if="slots.caption"
        class="flex min-w-0 flex-1 flex-col justify-end gap-1"
        :class="captionClass"
      >
        <slot name="caption" />
      </div>
    </div>

    <div
      v-else-if="slots.caption"
      class="flex min-w-0 flex-col gap-1 px-1 pt-4"
    >
      <slot name="caption" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, useSlots } from 'vue'

import type { VenueAccent } from '@/domains/shared/types/consts/pjazza/VenueAccent.consts.ts'

import { venueMonogram } from '@/domains/pjazza/utils/venueMonogram.ts'

/**
 * A venue's banner: its cover with the logo hanging off the corner, or its accent with the logo centred.
 *
 * `banner` heads a page, `strip` tops a card. The default slot sits over the banner, for a back
 * button; `caption` sits beside the logo, the way the directory card sets a venue's name.
 */
const props = withDefaults(
  defineProps<{
    name: string
    accent: VenueAccent
    logoUrl?: string | null
    coverUrl?: string | null
    coverSrcset?: string | null
    size?: 'banner' | 'strip'
  }>(),
  { size: 'banner' }
)

const slots = useSlots()

const coverFailed = ref(false)

const logoFailed = ref(false)

const hasCover = computed(() => !!props.coverUrl && !coverFailed.value)

const hasLogo = computed(() => !!props.logoUrl && !logoFailed.value)

const accentStyle = computed(() => ({ backgroundColor: `var(--pj-venue-${props.accent})` }))

const isStrip = computed(() => props.size === 'strip')

// The width the photo renders at. resources/views/pjazza.blade.php preloads a venue page's cover with the same
// value, so the browser picks the same srcset candidate for both and downloads the photo once.
const COVER_SIZES = '(min-width: 1280px) 1200px, 100vw'

const heightClass = computed(() => (isStrip.value ? 'h-32' : 'h-48 sm:h-64 lg:h-80'))

// A strip is too short for the solo size to keep any room above and below it.
const soloLogoClass = computed(() => (isStrip.value ? 'h-logo-card w-logo-card' : 'h-logo-card-solo w-logo-card-solo'))

// Pinned to the cover's foot by its centre, and inset to the cover's corner.
const logoClass = computed(() =>
  isStrip.value
    ? 'left-3 h-logo-card w-logo-card'
    : 'left-5 h-logo-banner w-logo-banner sm:left-8 sm:h-logo-banner-wide sm:w-logo-banner-wide'
)

// At least the half of the logo that hangs below the cover, so whatever follows clears it.
const rowClass = computed(() => (isStrip.value ? 'min-h-10 gap-3 px-3' : 'min-h-12 gap-6 px-5 sm:min-h-16 sm:px-8'))

const spacerClass = computed(() => (isStrip.value ? 'w-logo-card' : 'hidden sm:block sm:w-logo-banner-wide'))

// Beside the logo, a short caption ends level with its foot and a long one grows past it. Under it, on a
// phone's banner, the caption starts a gap below the logo's foot.
const captionClass = computed(() => (isStrip.value ? 'min-h-10 pt-2' : 'pt-16 sm:min-h-16 sm:pt-3'))
</script>
